
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import SearchIcon from '@mui/icons-material/Search';
import {
  TableRow,
  TableCell
} from '@mui/material';

import { BASE_URL, getAxiosConfig } from '../../../components/interface/Config';
import DateRangePicker from '../../../components/FormFilelds/DateRangePicker';
import IconButton from '../../../components/Buttons/SubjectiveIcons';
import ClearIcon from "@mui/icons-material/Clear";
import Chart from "../../../components/Chart";
import TableComponent from '../../../components/Table/TableComponent';
import TablePaginationComponent from '../../../components/TablePaginationComponent';
import ChartButton from '../../../components/Button/ChartButton';
import { HeartRate, ObjectiveResponse } from '../../../components/interface/ObjectiveInterface';

const HeartRateForm: React.FC<{ patientId: string }> = ({ patientId }) => {
  const [heartRateRecords, setHeartRateRecords] = useState<HeartRate[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [showChart, setShowChart] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'info' | 'warning' | 'error'>('success');

  const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    fetchHeartRates(dateRange.startDate, dateRange.endDate);
  }, [page, rowsPerPage, searchTerm]);

  const fetchHeartRates = async (startDate: string = '', endDate: string = '') => {
    setHeartRateRecords([]);
    setLoading(true);
    const authToken = sessionStorage.getItem('authToken');
    try {
      const response = await axios.get<ObjectiveResponse>(`${BASE_URL}/resource/vitals`, {
        params: {
          limit: rowsPerPage,
          page: page + 1,
          from: startDate || '',
          to: endDate || '',
          order_by: 'details->date',
          dir: 2,
          user_id: patientId,
          slug: 'heart-rate',
          searchkey: searchTerm || undefined,
        },
        headers: getAxiosConfig().headers,
      });

      const { vitals } = response.data.data;
      console.log('Fetched vitals:', vitals);
      const total = response.data.data.pagination!.total;
      console.log('Total records:', total);

      if (Array.isArray(vitals) && vitals.length > 0) {
        const formattedData: HeartRate[] = vitals.map((vital) => ({
          id: vital.id,
          user_id: vital.user_id,
          consult_id: vital.consult_id,
          peripheral_id: vital.peripheral_id,
          slug: vital.slug,
          details: {
            date: vital.details.date,
            time: vital.details.time,
            type: vital.details.device_type || '',
            heart: vital.details.heart,
            resultFile: vital.details.resultFile || null,
            interpretation: vital.details.interpretation || '',
            range_code: vital.details.range_code || '',
            heartRateFlag: vital.details.heartRateFlag || '',
            heartRateFlagColor: vital.details.heartRateFlagColor || '',
          },
          freeze: vital.freeze,
          document: vital.document || null,
        }));

        console.log('Formatted heart rate records:', formattedData);
        setHeartRateRecords(formattedData);
        setTotalRecords(total);
      } else {
        console.warn('No vitals found for the given filters.');
      }
    } catch (error) {
      console.warn('No vitals found for the selected date range');
      console.error('Error fetching heart rates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setDateRange({ startDate: '', endDate: '' });
    setPage(0); // Reset to the first page
    setRowsPerPage(5); // Reset rows per page if needed
    fetchHeartRates(); // Fetch all records again
    setFiltersApplied(false);
  };


  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'dd-MM-yyyy');
  };

  const renderRow = (item: HeartRate, index: number) => {
    return (
      <TableRow key={item.id}>
        <TableCell className="px-4 py-3 font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem'  }}> {page * rowsPerPage + index + 1}</TableCell>
        <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif', fontSize: '1rem'  }}>{formatDate(item .details.date)}</TableCell>
        <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem' }}>{item.details.time}</TableCell>
        <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem' }}>{item.details.type}</TableCell>
        <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem' }}>{item.details.heart}</TableCell>
        <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem' }}>{item.details.interpretation}</TableCell>
        {/* <TableCell>
                {item.document?.properties.file_path ? (
                  <a href={item.document.properties.file_path} target="_blank" rel="noopener noreferrer">
                    View File
                  </a>
                ) : (
                  'No file'
                )}
              </TableCell> */}
        <TableCell sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem' }}>
          <span
            style={{
              backgroundColor: item.details.range_code,
              padding: '4px 8px',
              borderRadius: '8px',
              color: '#FFFFFF'
            }}
          >
            {item.details.heartRateFlag}
          </span>
        </TableCell>
      </TableRow>
    )
  }

  const chartData = {
    labels: heartRateRecords.map((record) => record.details.date),
    datasets: [
      {
        label: 'Heart Rate (bpm)',
        data: heartRateRecords.map((record) => record.details.heart),
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderWidth: 2
      }
    ]
  }

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${context.raw} bpm`
        }
      }
    }
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between w-full gap-4 pb-4">
        <div className="flex flex-grow space-x-4">
          <DateRangePicker
            startDateId="HeartRate_Start_Date"
            endDateId="HeartRate_End_Date"
            onChange={(dates: { startDate: string; endDate: string }) => {
              setDateRange(dates);
              setFiltersApplied(true);
            }}
            value={dateRange}
          />
                <IconButton
                icon={<SearchIcon />}
                onClick={() => {
                  fetchHeartRates(dateRange.startDate, dateRange.endDate);
                  setFiltersApplied(true); // Mark filters as applied when search is clicked
                }}
              />

              {filtersApplied && (
                <IconButton
                  icon={<ClearIcon />}
                  onClick={handleClearFilters}
                />
              )}
          </div>
       
        <div className="flex space-x-4">
        <ChartButton
          text={showChart ? 'List' : 'Chart'}
          onClick={() => setShowChart((prev) => !prev)}
        />
       </div>
      </div>
      {loading ? (
        <div className="flex justify-center items-center h-64">
       
        </div>
      ) : showChart ? (
        <Chart data={chartData} options={chartOptions} />
      ) : (
        <>
          <TableComponent
            headers={[
              'S.No',
              'Date',
              'Time',
              'Type',
              'Heart Rate (bpm)',
              'Interpretation',
              'Result'
             
            ]}
            data={heartRateRecords}
            renderRow={renderRow}
            loading={loading}
            error={''}
          />
         
        </>
      )}
          <TablePaginationComponent
        count={totalRecords}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_event, newPage) => {
          setPage(newPage);
        }}
        onRowsPerPageChange={(event) => {
          const newRowsPerPage = parseInt(event.target.value, 10);
          setRowsPerPage(newRowsPerPage);
          setPage(0);
        }}
      />
   

    </div>
  )
}

export default HeartRateForm
